import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { toast } from 'sonner';
import { Send, Bell, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Notifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('active');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => { loadHistory(); }, [historyPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await adminAPI.getNotifications({ page: historyPage, limit: 10 });
      setHistory(data.notifications || []);
      setHistoryTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error('Fyll ut tittel og melding');
      return;
    }
    setSending(true);
    try {
      const result = await adminAPI.sendNotification({ title, body, target });
      toast.success(`Varsling sendt til ${result.target_count || 0} brukere`);
      setTitle('');
      setBody('');
      loadHistory();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const historyPages = Math.ceil(historyTotal / 10) || 1;

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Varsler</h1>
        <p className="text-xs sm:text-sm text-white/40 mt-1">Send push-varsler til brukere</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Compose */}
        <div data-testid="admin-notifications-compose-form" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-5">NY VARSLING</p>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">TITTEL</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ny uke, nye muligheter"
                className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 transition-[box-shadow] duration-180"
              />
            </div>
            <div>
              <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">MELDING</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Tempo har oppdatert planen din. Sjekk inn!"
                rows={4}
                className="w-full rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white p-4 placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 resize-y transition-[box-shadow] duration-180"
              />
            </div>
            <div>
              <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">MÅLGRUPPE</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { val: 'all', label: 'Alle' },
                  { val: 'active', label: 'Aktive (7d)' },
                  { val: 'inactive', label: 'Inaktive (14d)' },
                ].map(t => (
                  <button
                    key={t.val}
                    type="button"
                    onClick={() => setTarget(t.val)}
                    className={`h-10 rounded-[12px] text-sm font-medium border transition-[background-color,border-color,color] duration-180 ${
                      target === t.val
                        ? 'bg-[#CCFF00]/10 border-[#CCFF00]/30 text-[#CCFF00]'
                        : 'bg-transparent border-white/10 text-white/40 hover:text-white/60'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid="admin-notifications-send-button"
              type="submit"
              disabled={sending}
              className="w-full h-[52px] rounded-[14px] bg-[#CCFF00] text-black font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#D7FF3A] active:scale-[0.99] transition-[background-color,transform] duration-180 shadow-[0_8px_24px_rgba(204,255,0,0.12)] disabled:opacity-50"
            >
              {sending ? <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
              Send varsling
            </button>
          </form>
        </div>

        {/* History */}
        <div data-testid="admin-notifications-history-table" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-5">HISTORIKK</p>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-8 h-8 text-white/10 mx-auto mb-3" />
              <p className="text-sm text-white/30">Ingen varsler sendt ennå</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((n, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.03]">
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-sm text-white font-medium">{n.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      n.status === 'sent' ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#FF9F0A]/15 text-[#FF9F0A]'
                    }`}>{n.status || 'queued'}</span>
                  </div>
                  <p className="text-xs text-white/40 mb-2">{n.body}</p>
                  <div className="flex items-center gap-3 text-[10px] text-white/25">
                    <span>Mål: {n.target}</span>
                    <span>{n.target_count || 0} mottakere</span>
                    {n.created_at && <span>{new Date(n.created_at).toLocaleString('nb-NO')}</span>}
                  </div>
                </div>
              ))}
              {historyPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-white/25">Side {historyPage}/{historyPages}</p>
                  <div className="flex gap-1">
                    <button disabled={historyPage <= 1} onClick={() => setHistoryPage(p => p - 1)} className="p-1.5 rounded-lg text-white/30 hover:text-white disabled:opacity-20">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button disabled={historyPage >= historyPages} onClick={() => setHistoryPage(p => p + 1)} className="p-1.5 rounded-lg text-white/30 hover:text-white disabled:opacity-20">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
